import React, { useMemo } from "react";

/* ---------------- tax summary hook (unchanged logic) ---------------- */
function useTaxSummary(lineItems, discountPercent, taxApplicable) {
  return useMemo(() => {
    const n = (v) => Number.parseFloat(v || 0) || 0;
    const basicTotal = lineItems.reduce((s, it) => s + n(it.qty) * n(it.rate), 0);
    const discountAmt = basicTotal * (n(discountPercent) / 100);

    const byRate = new Map();
    lineItems.forEach((it) => {
      const basic = n(it.qty) * n(it.rate);
      const share = basicTotal > 0 ? (basic / basicTotal) * discountAmt : 0;
      const net = basic - share;
      const rate = taxApplicable ? n(it.taxper) : 0;
      const tax = net * (rate / 100);
      const key = rate.toFixed(2);
      const row = byRate.get(key) || { rate, taxable: 0, tax: 0 };
      row.taxable += net;
      row.tax += tax;
      byRate.set(key, row);
    });

    const rows = Array.from(byRate.values())
      .sort((a, b) => a.rate - b.rate)
      .map((r) => ({
        rate: r.rate,
        sgst: r.tax / 2,
        cgst: r.tax / 2,
        igst: 0,
        tax: r.tax,
        value: r.taxable,
      }));

    if (!rows.length) rows.push({ rate: "", sgst: 0, cgst: 0, igst: 0, tax: 0, value: 0 });
    return rows;
  }, [lineItems, discountPercent, taxApplicable]);
}

const money = (v) =>
  (Number.isFinite(+v) ? +v : 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/* ---------------- component ---------------- */
const InvoiceTotals = ({
  remarks, setRemarks,
  discounts, selectedDiscountCode, setSelectedDiscountCode, setDiscountPercent,
  calculatedData,
  lineItems, discountPercent, taxApplicable,
}) => {
  const taxRows = useTaxSummary(lineItems, discountPercent, taxApplicable);
  const hasPresets = Array.isArray(discounts) && discounts.length > 0;

  // keep dropdown + manual input in sync
  const handleDiscountSelect = (e) => {
    const code = e.target.value;
    setSelectedDiscountCode(code);
    const selected = discounts.find((d) => d.disccode === code);
    setDiscountPercent(selected ? parseFloat(selected.discper) : 0);
  };

  const handleDiscountInputChange = (e) => {
    const value = e.target.value;
    const percent = value === "" ? 0 : parseFloat(value);
    const safe = isNaN(percent) ? 0 : percent;
    setDiscountPercent(safe);
    const matchingPreset = (discounts || []).find((d) => parseFloat(d.discper) === safe);
    setSelectedDiscountCode(matchingPreset ? matchingPreset.disccode : "");
  };

  return (
    <div className="invoice-footer footer-with-tax">
      {/* LEFT: tax table + remarks */}
      <div className="left-block">
        <table className="tax-summary">
          <colgroup>
            <col style={{ width: "14%" }} />
            <col style={{ width: "17.2%" }} />
            <col style={{ width: "17.2%" }} />
            <col style={{ width: "17.2%" }} />
            <col style={{ width: "17.2%" }} />
            <col style={{ width: "17.2%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Tax%</th><th>SGST</th><th>CGST</th><th>IGST</th><th>TaxAmt</th><th>Value</th>
            </tr>
          </thead>
          <tbody>
            {taxRows.map((r, i) => (
              <tr key={i}>
                <td className="ta-center" data-label="Tax%">
                  <span className="cell-value">{r.rate === "" ? "" : Number(r.rate).toString()}</span>
                </td>
                <td className="ta-right" data-label="SGST"><span className="cell-value">{money(r.sgst)}</span></td>
                <td className="ta-right" data-label="CGST"><span className="cell-value">{money(r.cgst)}</span></td>
                <td className="ta-right" data-label="IGST"><span className="cell-value">{money(r.igst)}</span></td>
                <td className="ta-right" data-label="TaxAmt"><span className="cell-value">{money(r.tax)}</span></td>
                <td className="ta-right" data-label="Value"><span className="cell-value">{money(r.value)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="remarks-inline">
          <label htmlFor="remarks">Remarks</label>
          <textarea id="remarks" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </div>
      </div>

      {/* RIGHT: totals */}
      <div className="totals-section">
        <div className="totals-card">
          <div className="totals-row">
            <span>Subtotal</span>
            <span className="amount">₹{calculatedData.subtotal.toFixed(2)}</span>
          </div>

          {/* --- Discount row ------------------------------------------------ */}
          <div className={`discount-row ${hasPresets ? "has-preset" : ""}`}>
            <label htmlFor="discount-preset" className="discount-text">Discount</label>

            {hasPresets && (
              <select
                id="discount-preset"
                className="discount-select"
                value={selectedDiscountCode}
                onChange={handleDiscountSelect}
              >
                <option value="">(Select Preset)</option>
                {discounts.map((d) => (
                  <option key={d.disccode} value={d.disccode}>{d.discname}</option>
                ))}
              </select>
            )}

            <div className="discount-input-group">
              <input
                type="number"
                className="discount-input"
                value={discountPercent}
                onChange={handleDiscountInputChange}
                onWheel={(e) => e.currentTarget.blur()}
                step="0.01"
                placeholder="0"
                aria-label="Discount percent"
              />
              <span>%</span>
            </div>

            <span className="discount-amount">- ₹{calculatedData.discountAmt.toFixed(2)}</span>
          </div>
          {/* ----------------------------------------------------------------- */}

          <div className="totals-row">
            <span>Total Tax</span>
            <span className="amount">₹{calculatedData.totalTax.toFixed(2)}</span>
          </div>

          <div className="totals-row grand-total">
            <span>Grand Total</span>
            <span className="amount">₹{Math.round(calculatedData.grandTotal).toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceTotals;
